import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';

// Components
import SessionStart from './components/SessionStart';
import QuestionDisplay from './components/QuestionDisplay';
import ProgressDashboard from './components/ProgressDashboard';
import AssessmentMode from './components/AssessmentMode';

// Enable React Router future flags
window.__reactRouterV7_futureFlag = {
  v7_startTransition: true,
  v7_relativeSplatPath: true
};

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
  },
});

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAssessment, setIsAssessment] = useState(false);

  // Check for existing session on load
  useEffect(() => {
    const existingSession = localStorage.getItem('kbatSession');
    if (existingSession) {
      setSession(JSON.parse(existingSession));
    }
    setLoading(false);
  }, []);

  const startSession = async (studentData) => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || '';

      // Step 1: Login/create user
      const loginResponse = await axios.post(`${baseUrl}/api/user/login`, {
        username: studentData.username
      });

      const user = loginResponse.data.user;

      // Step 2: Start session
      const sessionResponse = await axios.post(`${baseUrl}/api/session/start`, {
        userId: user.id
      });

      const newSession = {
        sessionId: sessionResponse.data.session.id,
        userId: user.id,
        username: user.username,
        startTime: new Date().toISOString()
      };

      setSession(newSession);
      localStorage.setItem('kbatSession', JSON.stringify(newSession));

      if (studentData.mode === 'assessment') {
        setIsAssessment(true);
      } else {
        setIsAssessment(false);
      }

      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('Session start error:', err);
      setError('Failed to start session. Please try again.');
      setLoading(false);
    }
  };

  const endSession = () => {
    setSession(null);
    setIsAssessment(false);
    localStorage.removeItem('kbatSession');
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'Inter, sans-serif'
      }}>
        <div>Loading KBAT Math App...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'Inter, sans-serif',
        color: '#dc004e'
      }}>
        <div>
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => setError(null)}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div style={{
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default
        }}>
          <Routes>
            <Route
              path="/"
              element={
                session ? (
                  isAssessment ? (
                    <Navigate to="/assessment" replace />
                  ) : (
                    <Navigate to="/question" replace />
                  )
                ) : (
                  <SessionStart onStartSession={startSession} />
                )
              }
            />
            <Route
              path="/assessment"
              element={
                session ? (
                  <AssessmentMode
                    sessionId={session.sessionId}
                    onExit={endSession}
                  />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/question"
              element={
                session ? (
                  <QuestionDisplay
                    session={session}
                    onEndSession={endSession}
                  />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/progress"
              element={
                session ? (
                  <ProgressDashboard
                    session={session}
                    onEndSession={endSession}
                  />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;